import type { DalError, DalReturn } from "@/core/dal/data.type";
import { dalFormatErrorMessage } from "@/core/dal/helper";

/**
 * Error class thrown by unwrapDal when a DAL operation fails.
 * Carries the original DalError for programmatic access and
 * an i18n message key for display via next-intl's t().
 */
export class DalMutationError extends Error {
	readonly dalError: DalError;
	readonly i18nKey: string;

	constructor(dalError: DalError) {
		const key = dalFormatErrorMessage(dalError);
		super(key);
		this.name = "DalMutationError";
		this.dalError = dalError;
		this.i18nKey = key;
	}
}

/**
 * Unwraps a DalReturn for use inside React Query mutationFn.
 * Returns the data on success, throws DalMutationError on failure.
 *
 * @example
 * const submitSearch = useMutation({
 *   mutationFn: async (data) => {
 *     const result = await aircraftService.retrieveAvailableAircraft(data);
 *     return unwrapDal(result);
 *   },
 *   onError: (error) => {
 *     if (error instanceof DalMutationError) {
 *       setErrorMessage(t(error.i18nKey));
 *     }
 *   },
 * });
 */
export const unwrapDal = <T>(result: DalReturn<T, DalError>): T => {
	if (result.success) {
		return result.data;
	}
	throw new DalMutationError(result.error);
};
